import { Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
    message: string;
    type: ToastType;
    show: boolean;
    onClose: () => void;
    action?: {
        label: string;
        onClick: () => void;
    };
}

const ICONS = {
    success: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
    ),
    error: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
    ),
    warning: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
    ),
    info: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
    ),
};

const COLORS = {
    success: 'text-green-500 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    error: 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    warning: 'text-yellow-500 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20',
    info: 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
};

export function Toast({ message, type, show, onClose, action }: ToastProps) {

    return (
        <Transition
            show={show}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="pointer-events-auto">
                <div className={`w-full sm:w-[320px] shadow-lg rounded-xl ring-1 ring-black/5 dark:ring-white/10 backdrop-blur-sm ${COLORS[type]} transform transition-all duration-300 ease-out`}>
                    <div className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                                {ICONS[type]}
                            </div>
                            <div className="flex-1 min-w-0 pr-2">
                                <p className="text-sm font-medium leading-5">{message}</p>
                                {action && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            action.onClick();
                                        }}
                                        className="mt-1 text-sm font-medium hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current rounded px-2 py-1 -ml-2"
                                    >
                                        {action.label}
                                    </button>
                                )}
                            </div>
                            <div className="flex-shrink-0">
                                <button
                                    type="button"
                                    className="rounded-lg p-2 inline-flex text-current hover:opacity-75 active:scale-95 transition-all duration-200 select-none tap-highlight-none touch-manipulation min-h-[44px] min-w-[44px] items-center justify-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    );
}
